/**
 * Reports played games of booked campaigns to the ad backend (ructec Edge Function `spiel-werbung`, booking per game since
 * 2026-10-05): once per run – when it ends, a new one starts or the page is left – the bookings (`B_<booking>`) whose
 * poster was counted at least once in that run (≥ 2 s visible, on any surface), with a random run id so the backend counts
 * each run once. Nothing about the player is sent; house ads are never reported.
 */
const BOOKED = /^(B_[0-9a-f]{32})_(SEITENBANNER|WERBEBRUECKE|KREUZUNG)$/;
const MAX_PENDING = 10;

function runId(): string {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export class AdReporter {
  private run: string | null = null;
  private readonly seen = new Set<string>();
  private session: string | null = null;
  private readonly onLeave = () => this.endRun();

  constructor(
    private readonly url: string | null,
    private readonly fetchFn: typeof fetch = (...a) => fetch(...a),
  ) {
    if (!url) return;
    globalThis.addEventListener?.('pagehide', this.onLeave);
  }

  /** Signed session token from the manifest – without it the backend counts nothing, so nothing is sent. */
  setSession(session: string | null): void {
    this.session = session;
  }

  /** A run starts (a run still open – a restart mid-run – is reported first). */
  startRun(): void {
    this.endRun();
    this.run = runId();
  }

  record(campaignId: string): void {
    const booking = BOOKED.exec(campaignId)?.[1];
    if (this.url && this.run && booking) this.seen.add(booking);
  }

  /** Reports whose sending failed (network, 5xx) – sent again with the next report; the run id makes a repeat harmless. */
  private readonly pending: string[] = [];

  /** The run is over: one report with every booking seen in it (plus any report that failed before). */
  endRun(): void {
    const run = this.run;
    const bookings = [...this.seen];
    this.run = null;
    this.seen.clear();
    if (!this.url || !this.session) return;
    if (run && bookings.length > 0 && this.pending.length < MAX_PENDING) this.pending.push(JSON.stringify({ sitzung: this.session, lauf: run, spiel: bookings }));
    // one report per run end: the backend takes one run per session every 10 s, a second one right after would be dropped
    const next = this.pending.shift();
    if (next) this.send(next);
  }

  private send(body: string): void {
    const retry = () => {
      if (this.pending.length < MAX_PENDING) this.pending.unshift(body);
    };
    // text/plain keeps it a simple CORS request (no preflight); keepalive lets it finish while the page closes
    void this.fetchFn(this.url!, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'text/plain' } })
      .then((response) => {
        if (response.status >= 500) retry();
      })
      .catch(retry);
  }

  dispose(): void {
    this.endRun();
    globalThis.removeEventListener?.('pagehide', this.onLeave);
  }
}
